import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';

import FeedbackDialog from './components/FeedbackDialog';
import MoodEmoji, { RateFiveEmojiList } from './components/MoodEmojiList';
import { EmojiEnum } from './components/MoodEmojiList/consts';
import useFeedbackActions from './hooks/useFeedbackActions';
import { FeedbackContainerProps, FeedbackProps } from './types';

const Feedback = (props: FeedbackProps) => {
  const { questions } = props;
  const { t } = useTranslation();

  const {
    feedbackQuestionsObject,
    emojiResponse,
    onEmojiClick,
    onSubmit,
    dialogOpen,
    onDialogClose,
    feedbackTaken,
    isRatingOnFiveScale,
  } = useFeedbackActions(props);

  if (feedbackTaken) {
    return (
      <Typography color="text-text-secondary" variant="body-short-02">
        {`${t('Common:ThanksForTheFeedback')} ✨`}
      </Typography>
    );
  }

  return (
    <>
      {dialogOpen && questions.length > 1 && (
        <FeedbackDialog
          emoji={emojiResponse?.response as EmojiEnum}
          isRatingOnFiveScale={isRatingOnFiveScale}
          questionDescription={feedbackQuestionsObject.feedbackDescription.description}
          questionText={feedbackQuestionsObject.feedbackDescription.questionText}
          onClose={onDialogClose}
          onSubmit={onSubmit}
        />
      )}
      <Stack alignItems="center" className="xs:flex-col" justifyContent="between">
        <Typography
          className="mx-auto me-16 xs:mb-16 xs:me-auto"
          color="text-text-placeholder"
          variant="body-short-02">
          {feedbackQuestionsObject.feedbackRating.questionText}
        </Typography>
        {isRatingOnFiveScale ? (
          <RateFiveEmojiList className="gap-x-12" size={28} onClick={onEmojiClick} />
        ) : (
          <MoodEmoji activeEmoji={emojiResponse?.response as EmojiEnum} onClick={onEmojiClick} />
        )}
      </Stack>
    </>
  );
};

// to allow nullable feedback questions in props and handle properly the case when there are less than two questions
const FeedbackContainer = (props: FeedbackContainerProps) => {
  const { entityId, feedbackQuestions, entityType, containerClassName } = props;
  const { [FeatureFlags.DisableFeedback]: isFeedbackDisabled } = useFeatureFlags();

  if (!entityId || !entityType || isFeedbackDisabled) {
    return null;
  }

  const questions =
    feedbackQuestions && feedbackQuestions[0]?.questions ? feedbackQuestions[0]?.questions : [];

  if (!questions || questions.length < 2) {
    return null;
  }

  //QP: enhance - automatic hide after submitting
  return (
    <Stack className={containerClassName} justifyContent="center">
      <Feedback entityId={entityId} entityType={entityType} questions={questions} />
    </Stack>
  );
};

export default React.memo(FeedbackContainer);
